import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import RequireUser from "@/components/public/RequireUser";
import ImageUpload from "@/components/tournament/ImageUpload";
import { Fld } from "@/components/tournament/FormSteps";
import { inputCls as inp } from "@/components/tournament/constants";

const blank = () => ({ full_name: "", nickname: "", role: "", country: "" });

function MemberRow({ m, i, set, remove, canRemove }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-[1fr_1fr_0.8fr_0.8fr_auto] gap-2 items-center" data-testid={`member-row-${i}`}>
      <input required value={m.full_name} onChange={(e) => set({ full_name: e.target.value })} placeholder="Full name *" maxLength={80} className={inp} data-testid={`member-${i}-fullname`} />
      <input required value={m.nickname} onChange={(e) => set({ nickname: e.target.value })} placeholder="In-game nickname *" maxLength={40} className={inp} data-testid={`member-${i}-nickname`} />
      <input value={m.role} onChange={(e) => set({ role: e.target.value })} placeholder="Role (e.g. Duelist)" maxLength={40} className={inp} data-testid={`member-${i}-role`} />
      <input value={m.country} onChange={(e) => set({ country: e.target.value })} placeholder="Country" maxLength={60} className={inp} data-testid={`member-${i}-country`} />
      <button type="button" onClick={remove} disabled={!canRemove} className="p-2 text-white/40 hover:text-red-400 disabled:opacity-20 justify-self-end" data-testid={`member-${i}-remove`}><Trash2 size={16} /></button>
    </div>
  );
}

function Form({ t }) {
  const navigate = useNavigate();
  const [f, setF] = useState({ name: "", logo: "", country: "", description: "", members: Array.from({ length: Math.min(t.team_size || 1, 5) }, blank) });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [agree, setAgree] = useState(!t.terms);
  const setMember = (i, p) => setF({ ...f, members: f.members.map((m, idx) => (idx === i ? { ...m, ...p } : m)) });

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    if (!agree) { setErr("Please accept the terms & conditions."); return; }
    setBusy(true);
    try { await api.post(`/tournaments/${t.slug}/register`, f); toast.success("Your team is registered!"); navigate(`/tournaments/${t.slug}?registered=1`); }
    catch (e2) { const d = e2.response?.data?.detail; setErr(typeof d === "string" ? d : "Please complete all required fields."); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-6" data-testid="register-team-form">
      <div className="card-surface rounded-2xl p-6 grid md:grid-cols-[160px_1fr] gap-6">
        <Fld label="Team Logo"><ImageUpload value={f.logo} onChange={(logo) => setF({ ...f, logo })} aspect="aspect-square" label="Upload logo" testid="reg-logo-upload" /></Fld>
        <div className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Fld label="Team Name *"><input required minLength={2} maxLength={60} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={inp} data-testid="reg-team-name" /></Fld>
            <Fld label="Team Country"><input value={f.country} maxLength={60} onChange={(e) => setF({ ...f, country: e.target.value })} className={inp} placeholder="Indonesia" data-testid="reg-team-country" /></Fld>
          </div>
          <Fld label="Description"><textarea value={f.description} maxLength={1000} onChange={(e) => setF({ ...f, description: e.target.value })} rows={3} className={inp} placeholder="Tell us about your team" data-testid="reg-team-description" /></Fld>
        </div>
      </div>
      <div className="card-surface rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <div><h3 className="font-bold text-white">Team Members</h3><p className="text-xs text-white/40">Recommended: {t.team_size} players</p></div>
          <button type="button" onClick={() => setF({ ...f, members: [...f.members, blank()] })} disabled={f.members.length >= 20} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 text-white text-xs font-bold hover:bg-white/20" data-testid="add-member-btn"><Plus size={13} /> Add member</button>
        </div>
        <div className="space-y-2">{f.members.map((m, i) => <MemberRow key={i} m={m} i={i} set={(p) => setMember(i, p)} remove={() => setF({ ...f, members: f.members.filter((_, idx) => idx !== i) })} canRemove={f.members.length > 1} />)}</div>
      </div>
      {t.terms && (
        <div className="card-surface rounded-2xl p-6">
          <h3 className="font-bold text-white mb-2">Terms & Conditions</h3>
          <div className="max-h-48 overflow-y-auto text-sm text-white/60 whitespace-pre-line rounded-lg bg-black/30 p-4">{t.terms}</div>
          <label className="flex items-center gap-2 mt-3 text-sm text-white/80"><input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="w-4 h-4 accent-[hsl(var(--primary))]" data-testid="reg-agree" /> I agree to the terms & conditions</label>
        </div>
      )}
      {err && <div className="text-sm text-red-400 bg-red-500/10 rounded-lg px-3 py-2" data-testid="reg-error">{err}</div>}
      <button type="submit" disabled={busy} data-testid="reg-submit" className="w-full sm:w-auto px-8 py-3 rounded-full bg-[hsl(var(--primary))] text-white font-bold flex items-center justify-center gap-2 disabled:opacity-60 hover:brightness-110">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Register Team
      </button>
    </form>
  );
}

export default function RegisterTeam() {
  const { slug } = useParams();
  const [t, setT] = useState(null);
  const [missing, setMissing] = useState(false);
  useEffect(() => { api.get(`/tournaments/${slug}`).then((r) => setT(r.data)).catch(() => setMissing(true)); }, [slug]);
  if (missing) return <div className="py-24 text-center text-white"><h1 className="text-2xl font-bold">Tournament not found</h1></div>;
  if (!t) return <div className="py-24 text-center text-white/40">Loading…</div>;
  return (
    <div className="mx-auto max-w-4xl px-4 md:px-6 py-10" data-testid="register-team-page">
      <Link to={`/tournaments/${slug}`} className="eyebrow text-white/50 hover:text-white inline-flex items-center gap-1.5"><ArrowLeft size={13} /> {t.name}</Link>
      <h1 className="text-3xl md:text-4xl font-bold text-white mt-3">Register Your Team</h1>
      <p className="text-white/50 mt-1 mb-8">{t.game} · {t.name}</p>
      {!t.registration_open ? <div className="card-surface rounded-2xl p-10 text-center text-white/50" data-testid="reg-closed">Registration is closed for this tournament.</div>
        : <RequireUser message="Sign in with Google or email to register your team."><Form t={t} /></RequireUser>}
    </div>
  );
}
